// Universal Data Service - Handles ALL data with real-time cloud sync
import firebaseAuthService from './firebaseAuthService';
import { db } from '../config/firebase';
import { doc, setDoc, getDoc, updateDoc, FieldPath } from 'firebase/firestore';
import { contentKey } from './contentKey';

class UniversalDataService {
  constructor() {
    this.syncQueue = [];
    this.isSyncing = false;
    this.isOnline = navigator.onLine;
    this.autoSyncInterval = null;
    this.lastSyncTime = null;
    this.setupEventListeners();
    this.startPeriodicSync();
  }

  setupEventListeners() {
    window.addEventListener('online', () => {
      this.isOnline = true;
      this.processSyncQueue();
    });

    window.addEventListener('offline', () => {
      this.isOnline = false;
    });

    // Listen for visibility changes to sync when tab becomes active
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden && this.isOnline) {
        this.processSyncQueue();
      }
    });
  }

  startPeriodicSync() {
    // Sync every 30 seconds
    this.syncInterval = setInterval(() => {
      if (this.isOnline) {
        this.processSyncQueue();
      }
    }, 30000);
  }

  stopPeriodicSync() {
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
      this.syncInterval = null;
    }
  }

  // Generate storage key for farm-specific data
  getStorageKey(farmId, dataType) {
    return `${dataType}_${farmId}`;
  }

  // ULTIMATE SAVE - Saves ANY data type with TIMESTAMPS on every change
  async saveUniversalData(dataType, data) {
    const currentSession = firebaseAuthService.getCurrentSession();
    if (!currentSession?.farm?.id) {
      throw new Error('No farm session found');
    }

    const farmId = currentSession.farm.id;
    const storageKey = this.getStorageKey(farmId, dataType);
    const currentTimestamp = new Date().toISOString();
    
    console.log(`💾 ULTIMATE SAVE ${dataType} for farm ${farmId} at ${currentTimestamp}...`);
    
    // ADD TIMESTAMPS TO ALL DATA ITEMS
    let timestampedData = data;
    if (Array.isArray(data)) {
      // Add timestamp to each item in array
      timestampedData = data.map(item => ({
        ...item,
        lastModified: item.lastModified || currentTimestamp
      }));
    } else if (data && typeof data === 'object') {
      // Add timestamp to object
      timestampedData = {
        ...data,
        lastModified: data.lastModified || currentTimestamp
      };
    }
    
    // Nothing changed and it's already in the cloud: no write, so other devices aren't disturbed.
    try {
      const existing = JSON.parse(localStorage.getItem(storageKey));
      if (existing?.synced === true && contentKey(existing.data) === contentKey(timestampedData)) {
        return timestampedData;
      }
    } catch { /* unreadable local copy: save normally */ }

    // Always save to localStorage first (immediate)
    const dataWrapper = {
      data: timestampedData,
      lastModified: currentTimestamp,
      synced: false, // Mark as not synced for now
      farmId: farmId,
      dataType: dataType
    };

    localStorage.setItem(storageKey, JSON.stringify(dataWrapper));
    console.log(`✅ ${dataType} saved locally with TIMESTAMPS successfully`);

    // Update last save time for sync status
    localStorage.setItem('lastSaveTime', currentTimestamp);

    // IMMEDIATE FIREBASE SYNC - Changes sync automatically to Firebase
    try {
      console.log(`🔥 AUTO-SYNCING ${dataType} to Firebase immediately...`);
      await this.syncToCloud(farmId, dataType, timestampedData);
      
      // Mark as synced
      dataWrapper.synced = true;
      dataWrapper.lastSynced = currentTimestamp;
      localStorage.setItem(storageKey, JSON.stringify(dataWrapper));
      
      console.log(`✅ ${dataType} AUTO-SYNCED to Firebase successfully`);
      
      // Trigger cross-device sync notification
      window.dispatchEvent(new CustomEvent('cloudSyncComplete', { 
        detail: { farmId, dataType, timestamp: currentTimestamp } 
      }));
      
    } catch (error) {
      console.error(`❌ Auto-sync to Firebase failed for ${dataType}:`, error);
      // Add to sync queue as fallback
      this.addToSyncQueue(farmId, dataType, timestampedData);
      console.log(`📋 Added ${dataType} to sync queue as fallback`);
    }

    return timestampedData;
  }

  // UNIVERSAL LOAD - Loads ANY data type with cloud sync check
  async loadUniversalData(dataType) {
    const currentSession = firebaseAuthService.getCurrentSession();
    if (!currentSession?.farm?.id) {
      console.warn('No farm session found for loading data');
      return null;
    }

    const farmId = currentSession.farm.id;
    const storageKey = this.getStorageKey(farmId, dataType);
    
    console.log(`📥 Loading ${dataType} for farm ${farmId}...`);
    
    // For now, skip cloud sync and load from localStorage directly
    const localData = localStorage.getItem(storageKey);
    if (localData) {
      try {
        const parsed = JSON.parse(localData);
        console.log(`💾 ${dataType} loaded from local storage`);
        // Handle both wrapped format { data: [...] } (saved by universalDataService)
        // and direct format [...] (written by cloudSyncService.saveDataToLocalStorage
        // when no pre-existing wrapper is present on the device).
        if (parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed) && 'data' in parsed) {
          return parsed.data;
        }
        return parsed; // direct array or plain object
      } catch (error) {
        console.error(`Error parsing local data for ${dataType}:`, error);
        return null;
      }
    }

    console.log(`❌ No ${dataType} found in local storage`);
    return null;
  }

  // Add data to ULTIMATE sync queue with timestamps
  addToSyncQueue(farmId, dataType, data) {
    const existingIndex = this.syncQueue.findIndex(
      item => item.farmId === farmId && item.dataType === dataType
    );

    const currentTimestamp = new Date().toISOString();
    const syncItem = {
      farmId,
      dataType,
      data,
      timestamp: currentTimestamp,
      queuedAt: currentTimestamp
    };

    if (existingIndex >= 0) {
      // Keep original queue time but update timestamp
      syncItem.queuedAt = this.syncQueue[existingIndex].queuedAt;
      this.syncQueue[existingIndex] = syncItem;
      console.log(`📋 UPDATED ${dataType} in sync queue (originally queued at ${syncItem.queuedAt})`);
    } else {
      this.syncQueue.push(syncItem);
      console.log(`📋 ADDED ${dataType} to sync queue at ${currentTimestamp} (${this.syncQueue.length} items)`);
    }
  }

  // Process sync queue when online
  async processSyncQueue() {
    if (!this.isOnline || this.syncQueue.length === 0) return;

    console.log(`🔄 Processing ${this.syncQueue.length} items in sync queue...`);

    const itemsToSync = [...this.syncQueue];
    this.syncQueue = [];

    let successCount = 0;
    let failCount = 0;

    for (const item of itemsToSync) {
      try {
        await this.syncToCloud(item.farmId, item.dataType, item.data);
        
        // Mark as synced in localStorage
        const storageKey = this.getStorageKey(item.farmId, item.dataType);
        const localData = localStorage.getItem(storageKey);
        if (localData) {
          const parsed = JSON.parse(localData);
          parsed.synced = true;
          parsed.lastSynced = new Date().toISOString();
          localStorage.setItem(storageKey, JSON.stringify(parsed));
        }

        successCount++;
        console.log(`✅ Synced ${item.dataType} for farm ${item.farmId}`);
      } catch (error) {
        console.error(`❌ Failed to sync ${item.dataType}:`, error);
        // Re-add to queue for retry
        this.syncQueue.push(item);
        failCount++;
      }
    }

    if (successCount > 0) {
      console.log(`🎉 Successfully synced ${successCount} items`);
    }
    if (failCount > 0) {
      console.log(`⚠️ Failed to sync ${failCount} items (will retry)`);
    }
  }

  // Sync data to Firebase (ENHANCED: Save to both collections for compatibility)
  async syncToCloud(farmId, dataType, data) {
    try {
      console.log(`🔥 Syncing ${dataType} to Firebase for farm ${farmId}...`);
      
      // Each data type is written on its own, without touching the rest of the document,
      // so two devices saving different things at the same moment can't undo each other.
      const now = new Date().toISOString();
      const writeOneField = async (docRef, path, value) => {
        try {
          await updateDoc(docRef, path, value, 'lastModified', now);
        } catch (error) {
          if (error?.code !== 'not-found') throw error;
          // First save for this farm: create the document.
          const nested = path.segments.reduceRight((inner, key) => ({ [key]: inner }), value);
          await setDoc(docRef, { ...nested, lastModified: now }, { merge: true });
        }
      };

      await writeOneField(doc(db, 'farmData', farmId), new FieldPath(dataType), {
        data, lastModified: now, synced: true,
      });

      // Also saved to syncData, the format Force Download reads.
      const storageKey = this.getStorageKey(farmId, dataType);
      await writeOneField(doc(db, 'syncData', farmId), new FieldPath('farmData', storageKey), {
        actualData: data, dataType, lastModified: now, originalFormat: 'wrapped',
      });
      console.log(`✅ ${dataType} also synced to syncData collection for Force Download compatibility`);
      
      return true;
    } catch (error) {
      console.error(`❌ Failed to sync ${dataType} to Firebase:`, error);
      throw error;
    }
  }

  // Load data from Firebase (instead of Google Drive)
  async loadFromCloud(farmId, dataType) {
    try {
      console.log(`📥 Loading ${dataType} from Firebase for farm ${farmId}...`);
      
      const farmDocRef = doc(db, 'farmData', farmId);
      const farmDoc = await getDoc(farmDocRef);
      
      if (!farmDoc.exists()) {
        console.log(`📄 No Firebase data found for farm ${farmId}`);
        return null;
      }
      
      const farmData = farmDoc.data();
      const typeData = farmData[dataType];
      
      if (typeData && typeData.data) {
        console.log(`✅ Loaded ${dataType} from Firebase for farm ${farmId}`);
        return typeData.data;
      } else {
        console.log(`📄 No ${dataType} found in Firebase for farm ${farmId}`);
        return null;
      }
    } catch (error) {
      console.error(`❌ Failed to load ${dataType} from Firebase:`, error);
      throw error;
    }
  }

  // SPECIFIC DATA TYPE METHODS FOR EASY USAGE

  // Parcels Data
  async saveParcels(parcels) {
    return await this.saveUniversalData('parcelsData', parcels);
  }

  async loadParcels() {
    return await this.loadUniversalData('parcelsData') || [];
  }

  // Inventory Data
  async saveInventory(inventory) {
    return await this.saveUniversalData('inventoryData', inventory);
  }

  async loadInventory() {
    return await this.loadUniversalData('inventoryData') || { materials: [], equipments: [], invoices: [] };
  }

  // Tasks Data
  async saveTasks(tasks) {
    return await this.saveUniversalData('tasksData', tasks);
  }

  async loadTasks() {
    return await this.loadUniversalData('tasksData') || [];
  }

  // Fields Data
  async saveFields(fields) {
    return await this.saveUniversalData('fieldsData', fields);
  }

  async loadFields() {
    return await this.loadUniversalData('fieldsData') || [];
  }

  // Weather Data
  async saveWeather(weather) {
    return await this.saveUniversalData('weatherData', weather);
  }

  async loadWeather() {
    return await this.loadUniversalData('weatherData') || { current: {}, forecast: [] };
  }

  // Finances Data
  async saveFinances(finances) {
    return await this.saveUniversalData('financesData', finances);
  }

  async loadFinances() {
    return await this.loadUniversalData('financesData') || { revenue: {}, expenses: {}, profit: {} };
  }

  // Settings Data
  async saveSettings(settings) {
    return await this.saveUniversalData('settingsData', settings);
  }

  async loadSettings() {
    return await this.loadUniversalData('settingsData') || {};
  }

  // Seasons Data
  async saveSeasons(seasons) {
    return await this.saveUniversalData('seasonsData', seasons);
  }

  async loadSeasons() {
    return await this.loadUniversalData('seasonsData') || [];
  }

  // Company Settings
  async saveCompanySettings(settings) {
    return await this.saveUniversalData('companySettings', settings);
  }

  async loadCompanySettings() {
    return await this.loadUniversalData('companySettings') || {
      companies: [],
      defaultCompany: '',
      locations: [],
      defaultLocation: ''
    };
  }

  // Work Plans Data
  async saveWorkPlans(workPlans) {
    return await this.saveUniversalData('workPlansData', workPlans);
  }

  async loadWorkPlans() {
    return await this.loadUniversalData('workPlansData') || [];
  }

  // Stock Adjustment Data
  async saveStockAdjustment(stockData) {
    return await this.saveUniversalData('stockAdjustmentData', stockData);
  }

  async loadStockAdjustment() {
    return await this.loadUniversalData('stockAdjustmentData') || {
      companyConfigs: {},
      invoices: [],
      allocations: {}
    };
  }

  // ── NEW MODULES ─────────────────────────────────────────────────────────────

  // Motorina (Diesel Tank)
  async saveMotorina(data) { return this.saveUniversalData('motorinData', data); }
  async loadMotorina() {
    return await this.loadUniversalData('motorinData') || { tankCapacity: 15000, purchases: [], monthlyUsage: [] };
  }

  // Utilaje (Equipment with leasing/amortization)
  async saveUtilaje(data) { return this.saveUniversalData('utilajeData', data); }
  async loadUtilaje() { return await this.loadUniversalData('utilajeData') || []; }

  // Facturi (Invoices — drives Inventory stock & prices)
  async saveFacturi(data) { return this.saveUniversalData('facturiData', data); }
  async loadFacturi() { return await this.loadUniversalData('facturiData') || []; }

  // Produse Catalog (Inventory products catalog)
  async saveProduse(data) { return this.saveUniversalData('produseData', data); }
  async loadProduse() { return await this.loadUniversalData('produseData') || []; }

  // Lucrari (Field Operations — heart of cost system)
  async saveLucrari(data) { return this.saveUniversalData('lucrariData', data); }
  async loadLucrari() { return await this.loadUniversalData('lucrariData') || []; }

  // Get sync status for UI display
  getSyncStatus(dataType) {
    const currentSession = firebaseAuthService.getCurrentSession();
    if (!currentSession?.farm?.id) return 'no-session';

    const farmId = currentSession.farm.id;
    const storageKey = this.getStorageKey(farmId, dataType);
    const localData = localStorage.getItem(storageKey);
    
    if (!localData) return 'no-data';
    
    const parsed = JSON.parse(localData);
    
    if (!this.isOnline) return 'offline';
    if (parsed.synced) return 'synced';
    
    const isInQueue = this.syncQueue.some(
      item => item.farmId === farmId && item.dataType === dataType
    );
    
    return isInQueue ? 'pending' : 'local-only';
  }

  // Force sync all data for current farm
  async forceSyncAll() {
    const currentSession = firebaseAuthService.getCurrentSession();
    if (!currentSession?.farm?.id) {
      throw new Error('No farm session found');
    }

    const farmId = currentSession.farm.id;
    const dataTypes = [
      'parcelsData', 
      'inventoryData', 
      'stockAdjustmentData',
      'workPlansData', 
      'tasksData', 
      'fieldsData',
      'weatherData',
      'financesData',
      'settingsData',
      'seasonsData',
      'companySettings'
    ];
    
    console.log(`🔥 FORCE SYNCING ALL DATA to Firebase for farm ${farmId}...`);
    
    let syncedCount = 0;
    let errorCount = 0;
    
    for (const dataType of dataTypes) {
      try {
        const storageKey = this.getStorageKey(farmId, dataType);
        const localData = localStorage.getItem(storageKey);
        
        if (localData) {
          const parsed = JSON.parse(localData);
          const actualData = parsed.data || parsed;
          
          console.log(`🔄 Force syncing ${dataType}...`);
          await this.syncToCloud(farmId, dataType, actualData);
          
          // Mark as synced in localStorage
          if (parsed.data) {
            parsed.synced = true;
            parsed.lastSynced = new Date().toISOString();
            localStorage.setItem(storageKey, JSON.stringify(parsed));
          }
          
          syncedCount++;
          console.log(`✅ ${dataType} force synced successfully`);
        } else {
          console.log(`⚠️ No local data found for ${dataType}`);
        }
      } catch (error) {
        console.error(`❌ Failed to force sync ${dataType}:`, error);
        errorCount++;
      }
    }

    console.log(`🎉 FORCE SYNC COMPLETE! Synced: ${syncedCount}, Errors: ${errorCount}`);
    
    // Trigger global sync complete event
    window.dispatchEvent(new CustomEvent('forceSyncComplete', { 
      detail: { farmId, syncedCount, errorCount } 
    }));
    
    return { syncedCount, errorCount };
  }

  // Check if connected to Firebase
  async isCloudConnected() {
    try {
      // Check if we have a valid Firebase session
      const currentSession = firebaseAuthService.getCurrentSession();
      return currentSession && currentSession.farm && currentSession.farm.id;
    } catch (error) {
      console.error('Error checking Firebase connection:', error);
      return false;
    }
  }

  // Connect to Firebase (already connected via auth)
  async connectToCloud() {
    try {
      const currentSession = firebaseAuthService.getCurrentSession();
      if (currentSession && currentSession.farm && currentSession.farm.id) {
        console.log('✅ Firebase connection confirmed');
        return true;
      } else {
        console.log('❌ No valid Firebase session found');
        return false;
      }
    } catch (error) {
      console.error('Error connecting to Firebase:', error);
      return false;
    }
  }

  // Disconnect from Firebase (handled by auth service)
  async disconnectFromCloud() {
    try {
      console.log('📱 Firebase disconnect handled by auth service');
      return true;
    } catch (error) {
      console.error('Error disconnecting from Firebase:', error);
      return false;
    }
  }

  // Get all sync statuses for dashboard
  getAllSyncStatuses() {
    const dataTypes = [
      'parcelsData', 
      'inventoryData', 
      'tasksData', 
      'fieldsData',
      'weatherData',
      'financesData',
      'settingsData',
      'seasonsData'
    ];

    const statuses = {};
    for (const dataType of dataTypes) {
      statuses[dataType] = this.getSyncStatus(dataType);
    }

    return statuses;
  }

  // Clean up when component unmounts
  destroy() {
    this.stopPeriodicSync();
    window.removeEventListener('online', this.processSyncQueue);
    window.removeEventListener('offline', () => {});
    document.removeEventListener('visibilitychange', this.processSyncQueue);
  }
}

// Create singleton instance
const universalDataService = new UniversalDataService();

export default universalDataService; 